// analytics.ts - Unified analytics service for cross-platform tracking

import { v4 as uuidv4 } from "uuid"

// Define account types
export type AnalyticsAccountType = "main" | "event"

// Interface for analytics events
export interface AnalyticsEvent {
    name: string
    properties?: Record<string, any>
    accountType?: AnalyticsAccountType
    eventId?: string
}

// Interface for user identification
export interface UserIdentification {
    userId: string
    traits?: Record<string, any>
}

// Analytics providers
export enum AnalyticsProvider {
    GOOGLE_ANALYTICS = "google-analytics",
    FACEBOOK_PIXEL = "facebook-pixel",
    TIKTOK_PIXEL = "tiktok-pixel",
    SNAPCHAT_PIXEL = "snapchat-pixel",
    MICROSOFT_CLARITY = "microsoft-clarity",
    POSTHOG = "posthog",
}

/**
 * Platform-specific event name mapping
 * Maps internal event names to platform-specific standard event names
 */
const PLATFORM_EVENT_MAP: Record<string, Record<string, string>> = {
    facebook: {
        purchase_completed: "Purchase",
        payment_completed: "Purchase",
        checkout_started: "InitiateCheckout",
        event_viewed: "ViewContent",
        ticket_selected: "AddToCart",
        proceed_to_checkout: "InitiateCheckout",
        payment_failed: "Other",
        search_performed: "Search",
        add_to_cart: "AddToCart",
        begin_checkout: "InitiateCheckout",
        view_item: "ViewContent",
    },
    tiktok: {
        purchase_completed: "CompletePayment",
        payment_completed: "CompletePayment",
        checkout_started: "InitiateCheckout",
        event_viewed: "ViewContent",
        ticket_selected: "AddToCart",
        proceed_to_checkout: "PlaceAnOrder",
        payment_failed: "Other",
        search_performed: "Search",
        add_to_cart: "AddToCart",
        begin_checkout: "InitiateCheckout",
        view_item: "ViewContent",
    },
    snapchat: {
        purchase_completed: "PURCHASE",
        payment_completed: "PURCHASE",
        event_viewed: "VIEW_CONTENT",
        ticket_selected: "ADD_CART",
        proceed_to_checkout: "START_CHECKOUT",
        search_performed: "SEARCH",
        add_to_cart: "ADD_CART",
        begin_checkout: "START_CHECKOUT",
        view_item: "VIEW_CONTENT",
        payment_failed: "OTHER",
    },
    google: {
        purchase_completed: "purchase",
        payment_completed: "purchase",
        event_viewed: "view_item",
        ticket_selected: "add_to_cart",
        proceed_to_checkout: "begin_checkout",
        search_performed: "search",
        add_to_cart: "add_to_cart",
        begin_checkout: "begin_checkout",
        view_item: "view_item",
        payment_failed: "payment_failed",
    },
}

// Events that should be sent server-side for conversion tracking
const SERVER_SIDE_CONVERSION_EVENTS = [
    "purchase_completed",
    "payment_completed",
    "payment_failed",
    "purchase",
]

/**
 * Get platform-specific event name
 */
function getPlatformEventName(
    internalEventName: string,
    platform: keyof typeof PLATFORM_EVENT_MAP
): string {
    return PLATFORM_EVENT_MAP[platform]?.[internalEventName] || internalEventName
}

/**
 * Check if an event should be sent server-side for conversion tracking
 */
function shouldSendServerSide(eventName: string): boolean {
    return SERVER_SIDE_CONVERSION_EVENTS.includes(eventName)
}

const DEFAULT_CURRENCY = "SAR"

const VALUE_CANDIDATE_KEYS = [
    "value",
    "price",
    "total",
    "final_price",
    "amount",
]

const CONTENT_ID_CANDIDATE_KEYS = [
    "content_id",
    "event_id",
    "eventId",
    "item_id",
    "product_id",
]

const ENRICHED_COMMERCE_EVENTS = new Set([
    "purchase_completed",
    "payment_completed",
    "purchase",
    "begin_checkout",
    "checkout_started",
    "proceed_to_checkout",
    "ticket_selected",
    "add_to_cart",
    "view_item",
    "event_viewed",
])

function toNumber(value: unknown): number | undefined {
    if (typeof value === "number" && Number.isFinite(value)) return value
    if (typeof value === "string") {
        const normalized = value.replace(/,/g, "").trim()
        if (!normalized) return undefined
        const parsed = Number(normalized)
        if (Number.isFinite(parsed)) return parsed
    }
    return undefined
}

function getCurrency(properties: Record<string, any>): string {
    const rawCurrency =
        properties.currency ||
        properties.currency_code ||
        properties.order_currency ||
        DEFAULT_CURRENCY
    if (typeof rawCurrency !== "string") return DEFAULT_CURRENCY
    const normalized = rawCurrency.trim().toUpperCase()
    return normalized || DEFAULT_CURRENCY
}

function getValue(properties: Record<string, any>): number | undefined {
    for (const key of VALUE_CANDIDATE_KEYS) {
        const numeric = toNumber(properties[key])
        if (numeric !== undefined) return numeric
    }
    return undefined
}

function getPrimaryContentId(
    properties: Record<string, any>,
    fallbackEventId?: string
): string | undefined {
    for (const key of CONTENT_ID_CANDIDATE_KEYS) {
        const value = properties[key]
        if (typeof value === "string" && value.trim()) {
            return value.trim()
        }
    }
    const orderId = properties.order_id || properties.orderId
    if (typeof orderId === "string" && orderId.trim()) return orderId.trim()
    if (typeof fallbackEventId === "string" && fallbackEventId.trim()) {
        return fallbackEventId.trim()
    }
    return undefined
}

function getContentIds(
    properties: Record<string, any>,
    primaryContentId?: string
): string[] | undefined {
    const fromProperties = properties.content_ids
    if (Array.isArray(fromProperties)) {
        const normalized = fromProperties
            .map((item) => (typeof item === "string" ? item.trim() : ""))
            .filter(Boolean)
        if (normalized.length > 0) return normalized
    }

    if (Array.isArray(properties.items)) {
        const fromItems = properties.items
            .map((item: any) => {
                if (!item || typeof item !== "object") return ""
                const candidate =
                    item.item_id ||
                    item.id ||
                    item.product_id ||
                    item.content_id ||
                    item.event_id
                return typeof candidate === "string" ? candidate.trim() : ""
            })
            .filter(Boolean)
        if (fromItems.length > 0) return fromItems
    }

    if (primaryContentId) return [primaryContentId]
    return undefined
}

function normalizePhoneForMatching(phone: unknown): string | undefined {
    if (typeof phone !== "string") return undefined
    const trimmed = phone.trim()
    if (!trimmed) return undefined
    const normalized = trimmed.replace(/[^\d+]/g, "")
    return normalized || undefined
}

function getUserMatchData(properties: Record<string, any>): {
    email?: string
    phone_number?: string
    external_id?: string
} {
    const rawEmail =
        properties.userEmail || properties.email || properties.contact_email
    const rawPhone =
        properties.userPhone ||
        properties.phone ||
        properties.phone_number ||
        properties.contact_phone

    const email =
        typeof rawEmail === "string" && rawEmail.trim()
            ? rawEmail.trim().toLowerCase()
            : undefined
    const phone_number = normalizePhoneForMatching(rawPhone)
    const external_id =
        typeof properties.userId === "string" && properties.userId.trim()
            ? properties.userId.trim()
            : undefined

    return {
        ...(email ? { email } : {}),
        ...(phone_number ? { phone_number } : {}),
        ...(external_id ? { external_id } : {}),
    }
}

function enrichEventProperties(
    eventName: string,
    properties: Record<string, any>,
    eventId?: string
): Record<string, any> {
    if (!ENRICHED_COMMERCE_EVENTS.has(eventName)) {
        return properties
    }

    const primaryContentId = getPrimaryContentId(properties, eventId)
    const contentIds = getContentIds(properties, primaryContentId)
    const value = getValue(properties)
    const currency = getCurrency(properties)

    return {
        ...properties,
        ...(primaryContentId ? { content_id: primaryContentId } : {}),
        ...(contentIds ? { content_ids: contentIds } : {}),
        ...(value !== undefined ? { value } : {}),
        currency,
        content_type: properties.content_type || "product",
    }
}

/**
 * Analytics service that handles user identification and event tracking
 * across multiple platforms and account types.
 */
class AnalyticsService {
    private mainUserId: string | null = null
    private eventUserIds: Record<string, string> = {}
    private initialized = false
    private apiEndpoint: string | null = null
    /** Authenticated user ID — overrides anonymous UUID when set */
    private authenticatedUserId: string | null = null

    private readStorage(key: string): string | null {
        if (typeof window === "undefined") return null
        try {
            return window.localStorage.getItem(key)
        } catch {
            return null
        }
    }

    private writeStorage(key: string, value: string): void {
        if (typeof window === "undefined") return
        try {
            window.localStorage.setItem(key, value)
        } catch {
        }
    }

    constructor() {
        // Initialize the service when instantiated
        if (typeof window !== "undefined") {
            this.initialize()
        }
    }

    /**
     * Initialize the analytics service
     */
    public initialize(): void {
        if (this.initialized) return

        // Set API endpoint for sending analytics data
        this.apiEndpoint =
            process.env.NEXT_PUBLIC_ANALYTICS_API_ENDPOINT ||
            `${process.env.NEXT_PUBLIC_API_URL}analytics/track`

        // Generate or retrieve the main user ID
        this.mainUserId = this.getOrGenerateMainUserId()

        // Mark as initialized
        this.initialized = true

        // Send the main user ID to the API
        // this.syncUserIdToApi(this.mainUserId, "main")
    }

    /**
     * Get or generate the main user ID, stored in localStorage
     */
    private getOrGenerateMainUserId(): string {
        if (typeof window === "undefined") return ""

        const storageKey = "analytics_main_user_id"
        const storedId = this.readStorage(storageKey)

        if (storedId) {
            return storedId
        }

        // Generate a new UUID for the user
        const newId = uuidv4()
        this.writeStorage(storageKey, newId)
        return newId
    }

    /**
     * Get or generate an event-specific user ID, stored in localStorage
     */
    private getOrGenerateEventUserId(eventId: string): string {
        if (typeof window === "undefined" || !eventId) return ""

        const storageKey = `analytics_event_user_id_${eventId}`
        const storedId = this.readStorage(storageKey)

        if (storedId) {
            this.eventUserIds[eventId] = storedId
            return storedId
        }

        // Generate a new UUID for the event user
        const newId = uuidv4()
        this.writeStorage(storageKey, newId)
        this.eventUserIds[eventId] = newId
        return newId
    }

    /**
     * Send the user ID to the API for server-side tracking
     */
    // private syncUserIdToApi(
    //     userId: string,
    //     accountType: AnalyticsAccountType,
    //     eventId?: string
    // ): void {
    //     if (!this.apiEndpoint || !userId) return

    //     try {
    //         fetch(this.apiEndpoint, {
    //             method: "POST",
    //             headers: {
    //                 "Content-Type": "application/json",
    //             },
    //             body: JSON.stringify({
    //                 type: "identify",
    //                 userId,
    //                 accountType,
    //                 eventId,
    //                 timestamp: new Date().toISOString(),
    //             }),
    //         }).catch((e) => console.error("Error syncing user ID to API:", e))
    //     } catch (error) {
    //         console.error("Error syncing user ID to API:", error)
    //     }
    // }

    /**
     * Set the authenticated user ID for the current session.
     * When called, all subsequent events will use this ID instead of the
     * anonymous UUID, enabling accurate cross-platform user tracking for
     * logged-in users (e.g. mobile/webview where the user is always known).
     *
     * @param userId - The authenticated user's real ID
     * @param traits - Optional user traits to associate with this identity
     */
    public identifyUser(userId: string, traits?: Record<string, any>): void {
        if (!userId) return
        if (!this.initialized) this.initialize()

        this.authenticatedUserId = userId
        // Also update the main user ID so all subsequent calls use the real ID
        this.mainUserId = userId

        // Identify in PostHog with the real user ID
        if (typeof window !== "undefined" && window["posthog"]) {
            window["posthog"].identify(userId, traits)
        }
    }

    /**
     * Identify a user across analytics platforms
     */
    public identify(
        identification: UserIdentification,
        accountType: AnalyticsAccountType = "main",
        eventId?: string
    ): void {
        if (!this.initialized) this.initialize()

        const userId =
            identification.userId ||
            (accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId))

        if (!userId) return

        // Track in PostHog
        if (window["posthog"]) {
            if (accountType === "main" || !eventId) {
                window["posthog"].identify(userId, identification.traits)
            }
        }

        const matchData = getUserMatchData({
            userId,
            ...(identification.traits || {}),
        })
        if (Object.keys(matchData).length > 0) {
            this.dispatchWithRetry(
                () => Boolean(window["ttq"]),
                () => {
                    const pixelIds = window.__analyticsTikTokPixels || []
                    if (pixelIds.length > 0 && typeof window["ttq"].instance === "function") {
                        pixelIds.forEach((pixelId) => {
                            window["ttq"].instance(pixelId).identify(matchData)
                        })
                        return
                    }
                    window["ttq"].identify(matchData)
                }
            )
        }

        // Track in other providers as needed
        // this.syncUserIdToApi(userId, accountType, eventId)
    }

    /**
     * Track an event across analytics platforms
     * Automatically maps internal event names to platform-specific standard events
     */
    public track(event: AnalyticsEvent): void {
        if (!this.initialized) this.initialize()

        const { name, properties = {}, accountType = "main", eventId } = event
        const userId =
            accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId)

        if (!userId) return

        // Add user ID to properties
        const enhancedProperties = {
            ...properties,
            userId,
            accountType,
            ...(eventId ? { eventId } : {}),
        }
        const platformProperties = enrichEventProperties(
            name,
            enhancedProperties,
            eventId
        )

        if (this.shouldSkipDuplicateConversion(name, platformProperties)) {
            return
        }

        // Track in Google Analytics with platform-specific event name
        this.dispatchWithRetry(
            () => Boolean(window["gtag"]),
            () => {
            const gaEventName = getPlatformEventName(name, "google")
            window["gtag"]("event", gaEventName, platformProperties)
            },
            2
        )

        // Track in Facebook Pixel with platform-specific event name
        this.dispatchWithRetry(
            () => Boolean(window["fbq"]),
            () => {
                const fbEventName = getPlatformEventName(name, "facebook")
                const isStandardEvent =
                    PLATFORM_EVENT_MAP.facebook[name] !== undefined
                const pixelIds = window.__analyticsFacebookPixels || []

                if (pixelIds.length > 0) {
                    pixelIds.forEach((pixelId) => {
                        if (isStandardEvent) {
                            window["fbq"](
                                "trackSingle",
                                pixelId,
                                fbEventName,
                                platformProperties
                            )
                        } else {
                            window["fbq"](
                                "trackSingleCustom",
                                pixelId,
                                name,
                                platformProperties
                            )
                        }
                    })
                    return
                }

                if (isStandardEvent) {
                    window["fbq"]("track", fbEventName, platformProperties)
                } else {
                    window["fbq"]("trackCustom", name, platformProperties)
                }
            }
        )

        // Track in TikTok Pixel with platform-specific event name
        this.dispatchWithRetry(
            () => Boolean(window["ttq"]),
            () => {
                const matchData = getUserMatchData(platformProperties)
                const pixelIds = window.__analyticsTikTokPixels || []
                if (Object.keys(matchData).length > 0) {
                    if (
                        pixelIds.length > 0 &&
                        typeof window["ttq"].instance === "function"
                    ) {
                        pixelIds.forEach((pixelId) => {
                            window["ttq"].instance(pixelId).identify(matchData)
                        })
                    } else {
                        window["ttq"].identify(matchData)
                    }
                }

                const ttqEventName = getPlatformEventName(name, "tiktok")
                const trackedEventName =
                    PLATFORM_EVENT_MAP.tiktok[name] !== undefined
                        ? ttqEventName
                        : name

                if (
                    pixelIds.length > 0 &&
                    typeof window["ttq"].instance === "function"
                ) {
                    pixelIds.forEach((pixelId) => {
                        window["ttq"]
                            .instance(pixelId)
                            .track(trackedEventName, platformProperties)
                    })
                    return
                }

                window["ttq"].track(trackedEventName, platformProperties)
            }
        )

        // Track in Snapchat Pixel with platform-specific event name
        this.dispatchWithRetry(
            () => Boolean(window["snaptr"]),
            () => {
                const snapEventName = getPlatformEventName(name, "snapchat")
                window["snaptr"]("track", snapEventName, platformProperties)
            }
        )

        // Track in PostHog (uses internal event names)
        if (window["posthog"]) {
            window["posthog"].capture(name, platformProperties)
        }

        // Send to our own API for server-side tracking (for conversion events)
        if (shouldSendServerSide(name)) {
            this.trackToApi(name, platformProperties, accountType, eventId)
        }
    }

    private dispatchWithRetry(
        isReady: () => boolean,
        dispatch: () => void,
        attempts = 4
    ): void {
        if (isReady()) {
            dispatch()
            return
        }
        if (attempts <= 0 || typeof window === "undefined") return
        window.setTimeout(() => {
            this.dispatchWithRetry(isReady, dispatch, attempts - 1)
        }, 300)
    }

    private shouldSkipDuplicateConversion(
        eventName: string,
        properties: Record<string, any>
    ): boolean {
        if (typeof window === "undefined") return false
        if (
            eventName !== "payment_completed" &&
            eventName !== "purchase_completed" &&
            eventName !== "purchase"
        ) {
            return false
        }
        const orderId = properties.order_id || properties.orderId
        if (typeof orderId !== "string" || !orderId.trim()) return false
        const dedupeKey = `analytics_conversion_sent_${orderId.trim()}`
        const alreadySent = this.readStorage(dedupeKey) === "1"
        if (alreadySent) return true
        this.writeStorage(dedupeKey, "1")
        return false
    }

    /**
     * Track a page view across analytics platforms
     */
    public pageView(
        url: string,
        accountType: AnalyticsAccountType = "main",
        eventId?: string,
        userProperties?: Record<string, any>
    ): void {
        if (!this.initialized) this.initialize()

        const userId =
            accountType === "main"
                ? this.mainUserId
                : this.getEventUserId(eventId)

        if (!userId) return

        const properties = {
            url,
            userId,
            accountType,
            ...(eventId ? { eventId } : {}),
            ...(userProperties || {}),
        }

        // Track in Google Analytics
        if (window["gtag"]) {
            window["gtag"]("event", "page_view", properties)
        }

        // Track in Facebook Pixel
        if (window["fbq"]) {
            window["fbq"]("track", "PageView", properties)
        }

        // Track in TikTok Pixel
        if (window["ttq"]) {
            window["ttq"].page(properties)
        }

        // Track in Snapchat Pixel
        if (window["snaptr"]) {
            window["snaptr"]("track", "PAGE_VIEW", properties)
        }

        // Track in PostHog
        if (window["posthog"]) {
            window["posthog"].capture("$pageview", properties)
        }

        if (accountType === "event" && eventId) {
            this.trackToApi("event_viewed", properties, accountType, eventId)
        }
    }

    /**
     * Get the user ID for an event
     */
    public getEventUserId(eventId?: string): string | null {
        if (!eventId) return null

        return (
            this.eventUserIds[eventId] || this.getOrGenerateEventUserId(eventId)
        )
    }

    /**
     * Send event data to our API
     */
    private trackToApi(
        name: string,
        properties: Record<string, any>,
        accountType: AnalyticsAccountType,
        eventId?: string
    ): void {
        if (!this.apiEndpoint) return

        try {
            const eventData = { ...properties }
            if (eventData.order_id && !eventData.orderId) {
                eventData.orderId = eventData.order_id
            }
            fetch(this.apiEndpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    eventId,
                    eventName: name,
                    eventData,
                    userId: properties.userId,
                    sourceUrl:
                        typeof window !== "undefined"
                            ? window.location.href
                            : undefined,
                }),
            }).catch((e) => console.error("Error sending event to API:", e))
        } catch (error) {
            console.error("Error sending event to API:", error)
        }
    }
}

// Create a singleton instance
export const analytics = new AnalyticsService()

export default analytics
